import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';

// Scroll to top on every route change — fixes "scroll position preserved across navigation" bug.
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);
  return null;
};
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import FrameworkDetailPage from './pages/FrameworkDetailPage';
import UniverseDetailPage from './pages/UniverseDetailPage';
import ArtistsPage from './pages/ArtistsPage';
import CompaniesPage from './pages/CompaniesPage';
import AboutPage from './pages/AboutPage';
import StorePage from './pages/StorePage';
import CMSDashboard from './pages/CMSPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import ContentItemPage from './pages/ContentItemPage';
import UserManagementPage from './pages/UserManagementPage';
import ArtistEditorPage from './pages/ArtistEditorPage';
import ProjectEditorPage from './pages/ProjectEditorPage';
import CompanyEditorPage from './pages/CompanyEditorPage';
import AssociationRequestsPage from './pages/AssociationRequestsPage';
import SiteSettingsPage from './pages/SiteSettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import SearchPage from './pages/SearchPage';
import NewsletterPage from './pages/NewsletterPage';
import BulkImageUpdaterPage from './pages/BulkImageUpdaterPage';
import PrivateOverviewPage from './pages/PrivateOverviewPage';
import OverviewSettingsPage from './pages/OverviewSettingsPage';
import HomepageSectionsPage from './pages/HomepageSectionsPage';
import BrandSitesPage from './pages/BrandSitesPage';
import BrandSiteEditorPage from './pages/BrandSiteEditorPage';
import BTTAdminDashboard from './pages/BTTAdminDashboard';
import { DynamicBrandSitePage } from './pages/BrandSitePages';
import { Toaster } from './components/ui/sonner';
import { SiteProvider, useSite } from './contexts/SiteContext';
import BTTApp from './sites/btt/BTTApp';
import './App.css';

// Router wrapper to handle auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (auth callback) - must be synchronous
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check if this is a CMS route (no nav/footer)
  const isCMSRoute = location.pathname.startsWith('/cms');
  
  // Private overview page - standalone, no nav/footer
  const isPrivateOverview = location.pathname === '/overview';

  if (isCMSRoute) {
    return (
      <Routes>
        <Route path="/cms/login" element={<LoginPage />} />
        <Route path="/cms" element={<CMSDashboard />} />
        <Route path="/cms/users" element={<UserManagementPage />} />
        <Route path="/cms/artist/:artistId" element={<ArtistEditorPage />} />
        <Route path="/cms/project/:projectId" element={<ProjectEditorPage />} />
        <Route path="/cms/company/:companyId" element={<CompanyEditorPage />} />
        <Route path="/cms/requests" element={<AssociationRequestsPage />} />
        <Route path="/cms/settings" element={<SiteSettingsPage />} />
        <Route path="/cms/newsletter" element={<NewsletterPage />} />
        <Route path="/cms/bulk-images" element={<BulkImageUpdaterPage />} />
        <Route path="/cms/overview-settings" element={<OverviewSettingsPage />} />
        <Route path="/cms/homepage-sections" element={<HomepageSectionsPage />} />
        <Route path="/cms/brand-sites" element={<BrandSitesPage />} />
        <Route path="/cms/brand-sites/:siteId" element={<BrandSiteEditorPage />} />
        <Route path="/cms/btt" element={<BTTAdminDashboard />} />
        <Route path="/cms/*" element={<CMSDashboard />} />
      </Routes>
    );
  }

  // Private overview - standalone page without nav/footer
  if (isPrivateOverview) {
    return (
      <Routes>
        <Route path="/overview" element={<PrivateOverviewPage />} />
      </Routes>
    );
  }

  // Dynamic brand sites - check if path matches a brand site
  // We'll handle this more dynamically by adding a catch-all brand site route
  // For now, keep selina and bethetrain as known routes plus a dynamic catch

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navigation />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:slug" element={<ProjectDetailPage />} />
          <Route path="/project/:slug" element={<ProjectDetailPage />} />
          <Route path="/frameworks/:slug" element={<FrameworkDetailPage />} />
          <Route path="/universes/:slug" element={<UniverseDetailPage />} />
          <Route path="/artists" element={<ArtistsPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/store" element={<StorePage />} />
          {/* Canonical Content Item Pages - culturalrevolution.com/slug */}
          <Route path="/:slug" element={<ContentItemPage />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
}

// Brand Site Wrapper - dynamically checks if slug is a brand site (for CR site only)
function BrandSiteRouter() {
  const location = useLocation();
  const [routeType, setRouteType] = useState(null); // 'brand' | 'app' | null
  const [brandSlugsCache, setBrandSlugsCache] = useState(null);
  
  useEffect(() => {
    const pathSlug = location.pathname.split('/')[1];
    
    // Skip check for known non-brand routes
    const knownRoutes = ['cms', 'overview', 'search', 'projects', 'project', 'artists', 'companies', 'about', 'store', 'frameworks', 'universes', ''];
    if (knownRoutes.includes(pathSlug)) {
      setRouteType('app');
      return;
    }
    
    const checkRoute = async () => {
      try {
        // First check cached slugs
        if (brandSlugsCache && brandSlugsCache.includes(pathSlug)) {
          setRouteType('brand');
          return;
        }
        
        // Fetch brand sites list
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand-sites/public`);
        if (res.ok) {
          const sites = await res.json();
          const slugs = sites.map(s => s.slug);
          setBrandSlugsCache(slugs);
          
          if (slugs.includes(pathSlug)) {
            setRouteType('brand');
            return;
          }
        }
        
        // If not in list, check if it's a brand site directly (handles newly created sites)
        const directCheck = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand-sites/${pathSlug}`);
        if (directCheck.ok) {
          const site = await directCheck.json();
          if (site && site.slug) {
            setRouteType('brand');
            return;
          }
        }
        
        // Not a brand site, render as app
        setRouteType('app');
      } catch (err) {
        console.error('Route check failed:', err);
        // On error, default to app router (will show 404 for unknown routes)
        setRouteType('app');
      }
    };
    
    checkRoute();
  }, [location.pathname, brandSlugsCache]);
  
  // Show nothing briefly while determining route
  if (routeType === null) {
    return null;
  }
  
  // Render brand site without CR navigation
  if (routeType === 'brand') {
    return <DynamicBrandSitePage />;
  }
  
  // Render normal app with CR navigation
  return <AppRouter />;
}

// Main Site Router - switches between CR and BTT based on site config
function SiteRouter() {
  const { siteId, loading, isBTT } = useSite();
  const location = useLocation();
  
  // CMS routes should always use CR app (regardless of site)
  const isCMSRoute = location.pathname.startsWith('/cms');
  const isPrivateOverview = location.pathname === '/overview';
  
  if (isCMSRoute || isPrivateOverview) {
    return <AppRouter />;
  }
  
  // Show minimal loading state while determining site
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }
  
  // Render BTT app for Be The Train site
  if (isBTT) {
    return <BTTApp />;
  }
  
  // Render CR app with brand site routing
  return <BrandSiteRouter />;
}

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <SiteProvider>
          <SiteRouter />
          <Toaster position="top-right" />
        </SiteProvider>
      </Router>
    </HelmetProvider>
  );
}
